/**
 * Organization invitation schemas for validation
 */

import { z } from 'zod';
import { PlatformRoleKeySchema, RoleKeySchema } from './roles.js';

/**
 * THE invitation status alphabet — one value per state, one name per value.
 *
 * Foundations 2 (Users rail), B2c-T2. principles.md #1 and #5.
 *
 * ── WHAT WAS WRONG ────────────────────────────────────────────────────────
 *
 * The state had TWO names. The store, this schema, the API response and the
 * query filter all said `cancelled`; every sentence a person read said
 * "revoked" — the row action ("Revoke invitation"), the confirmation, the
 * toast, the tab and the status pill's label
 * (`web/src/routes/users/invitations.tsx:171` rendered the `cancelled` tab as
 * "Revoked"). Three files carried a comment explaining the mismatch
 * ("the schema has NO `revoked` value — the UI's Revoke action maps to
 * `cancelled`"), which is the tell: a translation table nobody needs.
 *
 * ── THE MODEL NOW ─────────────────────────────────────────────────────────
 *
 * ONE name, and it is `revoked` — founder decision 15 (2026-09-17), the word
 * the UI already showed. c32 renames the `invitation_status` enum value, and
 * old data is disposable (decision 14), so the rename needs no
 * data-preservation path beyond c32r. `cancelled` survives nowhere: not as a
 * stored value, not as an API literal, not as a UI label.
 *
 * `revoked` is also the word the lifecycle table uses for the state the
 * revoked invitee's MEMBERSHIP lands in — `invited_revoked`
 * (`user-lifecycle.ts`) — so the two halves of one action read the same.
 */
export const INVITATION_STATUSES = [
  'pending',
  'accepted',
  'declined',
  'expired',
  'revoked',
] as const;

export const invitationStatusSchema = z.enum(INVITATION_STATUSES);
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

/**
 * Create invitation request.
 *
 * `role` IS A SHIPPED PLATFORM ROLE KEY, not any well-formed key (B1-T6 review
 * BF-04). c27 converted `organization_invitations.role` off the
 * `organization_role` enum to TEXT holding the key, deliberately without a
 * CHECK — "enforcement stays where it already is and already throws" — but for
 * the CREATE boundary that enforcement did not exist: `RoleKeySchema` is the
 * permissive `/^[a-z][a-z0-9_]{0,62}$/`, and the enum had been the last
 * validator on the column. So `{"role":"engineer"}` stored a 201 that could
 * never be accepted: the accept path refuses any key that is not a platform
 * role, permanently, for every retry, with no route that can repair the row.
 * Fail closed at the boundary instead (principles.md #4), in the ONE schema
 * the create route validates against — Zod is the source of types.
 *
 * The RESPONSE schema below keeps `RoleKeySchema`: a row stored before this
 * has whatever it has, and reading it back must not 500.
 */
export const createInvitationSchema = z.object({
  email: z.string().email().transform((e) => e.toLowerCase().trim()),
  role: PlatformRoleKeySchema.default('member'),
});
export type CreateInvitationInput = z.infer<typeof createInvitationSchema>;

/**
 * ONE invitation row as `GET /api/v1/users/invitations` answers it (USR-F1-T2).
 *
 * This is THE wire shape: the route validates its 200 against it on the way
 * out and the web derives its row type from it (`z.infer`). It used to be
 * declared three times — a route-local schema in users.ts, a hand-typed
 * interface in the web with a four-value role union, and this one, which
 * described fields nothing emitted. `tokenHash` is deliberately absent (the
 * raw token is returned only at create and at resend). `email` is a plain
 * string: a row stored before a validator existed must still read back.
 */
export const invitationResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  role: RoleKeySchema,
  status: invitationStatusSchema,
  invitedBy: z.object({
    id: z.string().uuid(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string(),
  }),
  expiresAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type InvitationResponse = z.infer<typeof invitationResponseSchema>;

const invitationCountShape = Object.fromEntries(
  INVITATION_STATUSES.map((status) => [status, z.number().int().nonnegative()]),
) as Record<InvitationStatus, z.ZodNumber>;

/**
 * The tenant's invitations per status, after the expiry sweep, NOT narrowed by
 * the list's status / q / email (the tab badge reads "how many invitations are
 * in this state"). Every key of INVITATION_STATUSES is present (0 when none);
 * `all` is their sum. The server counts; the browser renders.
 */
export const invitationCountsSchema = z.object({
  ...invitationCountShape,
  all: z.number().int().nonnegative(),
});
export type InvitationCounts = z.infer<typeof invitationCountsSchema>;

/** Invitation list response — one page of the bucket, plus the tenant counts. */
export const invitationListResponseSchema = z.object({
  data: z.array(invitationResponseSchema),
  pagination: z.object({
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
    totalPages: z.number(),
  }),
  counts: invitationCountsSchema,
});
export type InvitationListResponse = z.infer<typeof invitationListResponseSchema>;

/** Accept invitation request */
export const acceptInvitationSchema = z.object({
  token: z.string().min(1, 'Invitation token is required'),
});
export type AcceptInvitationInput = z.infer<typeof acceptInvitationSchema>;
